import {
  Customer,
  CustomerGroup,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerFilters,
  SalesHistoryFilters,
  InvoiceFilters,
  ReceivableFilters,
  CustomerListResponse,
  CustomerDetailResponse,
  SalesHistoryListResponse,
  InvoiceListResponse,
  ReceivableListResponse,
  CustomerStatsResponse,
  ExportCustomerOptions,
  CustomerDebtReportQuery,
  CustomerDebtReportResponse,
  CustomerDebtSummaryResponse,
} from '@/types/customer';

import { api } from '@/api/axios';
import { 
  mapCategoryIdToCustomerType, 
  mapCustomerTypeToCategoryId 
} from '@/constants/categoryTypes';

// API Response Types (based on backend DTOs)
interface ApiCustomerResponse {
  id: string;
  code: string;
  category_group_id: string;
  category_group_name: string;
  category_type_id: string;
  category_type_name: string;
  name: string;
  gender: string;
  birth: string;
  phone: string;
  email: string;
  facebook: string;
  company: string;
  tax: string;
  address: {
    province: string;
    city: string;
    district: string;
    commune: string;
    street_address: string;
  };
  note: string;
  debit: number;
  total_sell: number;
  assigned_driver_id?: string;
  assigned_driver_name?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface ApiStandardResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

// Customer Type Statistics Response
interface ApiCustomerTypeStatsResponse {
  customer_type_id: string;
  customer_type_name: string;
  count: number;
}

// Utility functions
const mapApiCustomerToFrontend = (apiCustomer: ApiCustomerResponse): Customer => {
  // Map customer type based on category IDs
  let customerType: 'individual' | 'company' = mapCategoryIdToCustomerType(apiCustomer.category_type_id);
  // Override to company if company name is provided
  if (apiCustomer.company && apiCustomer.company.trim() !== '') {
    customerType = 'company';
  }

  return {
    id: apiCustomer.id,
    code: apiCustomer.code,
    name: apiCustomer.name,
    phone: apiCustomer.phone || '',
    email: apiCustomer.email || '',
    customerType,
    companyName: apiCustomer.company || undefined,
    address: {
      city: apiCustomer.address?.province || '', // Backend 'province' maps to frontend 'city'
      district: apiCustomer.address?.city || '', // Backend 'city' maps to frontend 'district'
      details: [
        apiCustomer.address?.district,
        apiCustomer.address?.commune,
        apiCustomer.address?.street_address
      ].filter(Boolean).join(', '),
    },
    notes: apiCustomer.note || undefined,
    status: 'active', // Default since backend doesn't have status field
    createdBy: 'System', // Backend doesn't track this
    createdAt: apiCustomer.createdAt,
    updatedAt: apiCustomer.updatedAt,
    gender: (apiCustomer.gender && ['male', 'female', 'other'].includes(apiCustomer.gender)) 
      ? apiCustomer.gender as any 
      : undefined,
    birthday: apiCustomer.birth && apiCustomer.birth !== '' ? apiCustomer.birth : undefined,
    facebookLink: apiCustomer.facebook || undefined,
    taxCode: apiCustomer.tax || undefined,
    customerGroupId: apiCustomer.category_group_id,
    customerGroupName: apiCustomer.category_group_name || undefined,
    customerTypeName: apiCustomer.category_type_name || undefined,
    categoryTypeId: apiCustomer.category_type_id, // Include backend category type ID
    totalPurchases: apiCustomer.total_sell || 0,
    totalSell: apiCustomer.total_sell || 0,
    totalDebt: apiCustomer.debit || 0,
    debit: apiCustomer.debit || 0, // Current debt amount
    lastPurchaseDate: undefined, // Backend doesn't provide this
    paymentStatus: (apiCustomer.debit || 0) > 0 ? 'pending' : 'paid',
    assignedDriverId: apiCustomer.assigned_driver_id,
    assignedDriverName: apiCustomer.assigned_driver_name,
  };
};

const mapFrontendCustomerToApi = (customer: CreateCustomerRequest) => {
  return {
    category_group_id: customer.categoryGroupId || 'CAT015', // Default customer group
    category_type_id: customer.categoryTypeId, // Use backend category type ID directly
    name: customer.name,
    gender: customer.gender || '',
    birth: customer.birthday && customer.birthday !== '' ? customer.birthday : '',
    phone: customer.phone,
    email: customer.email || '',
    facebook: customer.facebookLink || '',
    company: customer.companyName || '',
    tax: customer.taxCode || '',
    debit: customer.debit || 0, // Current debt amount
    address: {
      province: customer.address?.city || '', // Frontend 'city' maps to backend 'province'
      city: customer.address?.district || '', // Frontend 'district' maps to backend 'city'
      district: '', // Will be populated from address details if needed
      commune: '',
      street_address: customer.address?.details || '',
    },
    note: customer.notes || '',
  };
};

export const customerApi = {
  // Get customers list with filters and pagination
  getCustomers: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: CustomerFilters
  ): Promise<CustomerListResponse> => {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      // Add filters based on backend API
      if (filters?.search) {
        params.append('search', filters.search);
      }
      
      if (filters?.customerType && filters.customerType.length > 0) {
        // Map frontend customer types to backend category type IDs
        const categoryTypeIds = filters.customerType.map(mapCustomerTypeToCategoryId);
        // Send as comma-separated values for backend to handle efficiently
        params.append('category_type_id', categoryTypeIds.join(','));
      }

      // Handle direct category type ID filtering (for dynamic tabs)
      if (filters?.categoryTypeId && filters.categoryTypeId.length > 0) {
        // Send as comma-separated values for backend to handle efficiently
        params.append('category_type_id', filters.categoryTypeId.join(','));
      }
      
      if (filters?.customerGroupId) {
        params.append('category_group_id', filters.customerGroupId);
      }
      
      if (filters?.gender && filters.gender.length > 0) {
        filters.gender.forEach(gender => params.append('gender', gender));
      }

      const response = await api.get<ApiPaginatedResponse<ApiCustomerResponse>>(`/customers?${params.toString()}`);
      
      // Map API response to frontend format with null checks
      const apiData = response.data?.data || [];
      const customers = apiData.map(mapApiCustomerToFrontend);
      
      return {
        data: customers,
        total: response.data?.total || 0,
        page: response.data?.page || page,
        pageSize: response.data?.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch customers');
    }
  },

  // Get single customer by code
  getCustomer: async (code: string): Promise<Customer> => {
    try {
      const response = await api.get<ApiStandardResponse<ApiCustomerResponse>>(`/customers/${code}`);
      if (!response.data?.data) {
        throw new Error(`Customer with code ${code} not found`);
      }
      return mapApiCustomerToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error fetching customer:', error);
      throw new Error(error.apiError?.message || error.message || `Customer with code ${code} not found`);
    }
  },

  // Get customer detail with sales history, invoices, and receivables
  getCustomerDetail: async (code: string): Promise<CustomerDetailResponse> => {
    try {
      // Get customer details
      const customer = await customerApi.getCustomer(code);
      
      // For now, return customer with empty related data as backend doesn't have these endpoints yet
      return {
        customer,
        salesHistory: [],
        invoices: [],
        receivables: [],
        totalSales: customer.totalPurchases || 0,
        totalInvoices: 0,
        totalReceivables: customer.totalDebt || 0,
      };
    } catch (error: any) {
      console.error('Error fetching customer detail:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch customer detail');
    }
  },

  // Create new customer
  createCustomer: async (data: CreateCustomerRequest): Promise<Customer> => {
    try {
      const apiData = mapFrontendCustomerToApi(data);
      const response = await api.post<ApiStandardResponse<ApiCustomerResponse>>('/customers', apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiCustomerToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error creating customer:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to create customer');
    }
  },

  // Update customer
  updateCustomer: async (data: UpdateCustomerRequest & { code: string }): Promise<Customer> => {
    try {
      const { code, ...updateData } = data;

      // Add debug logging to help identify issues
      console.log('Customer update data received:', updateData);
      console.log('Debit field value:', updateData.debit);

      // Create a proper update request with all required fields
      const apiData = {
        category_group_id: updateData.categoryGroupId || 'CAT015',
        category_type_id: updateData.categoryTypeId || '',
        name: updateData.name || '',
        gender: updateData.gender || '',
        birth: (updateData.birthday && updateData.birthday !== '') ? updateData.birthday : '',
        phone: updateData.phone || '',
        email: updateData.email || '',
        facebook: updateData.facebookLink || '',
        company: updateData.companyName || '',
        tax: updateData.taxCode || '',
        debit: updateData.debit || 0, // Current debt amount
        address: {
          province: updateData.address?.city || '', // Frontend 'city' maps to backend 'province'
          city: updateData.address?.district || '', // Frontend 'district' maps to backend 'city'
          district: '',
          commune: '',
          street_address: updateData.address?.details || '',
        },
        note: updateData.notes || '',
      };

      // Debug log the final API payload
      console.log('Customer update API payload:', apiData);
      
      const response = await api.put<ApiStandardResponse<ApiCustomerResponse>>(`/customers/${code}`, apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiCustomerToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error updating customer:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to update customer');
    }
  },

  // Delete customer
  deleteCustomer: async (code: string): Promise<void> => {
    try {
      await api.delete(`/customers/${code}`);
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to delete customer');
    }
  },

  // Get customer groups (using categories API)
  getCustomerGroups: async (): Promise<CustomerGroup[]> => {
    try {
      // Get customer group categories from categories API
      const response = await api.get<ApiPaginatedResponse<any>>('/categories?type=customer_group');
      
      const categories = response.data?.data || [];
      return categories.map((category: any) => ({
        id: category.code || category.id,
        name: category.name || '',
        description: category.description || '',
        discountPercentage: 0, // Not available in backend
        customerCount: 0, // Not available in backend
        createdAt: category.createdAt || new Date().toISOString(),
        updatedAt: category.updatedAt || new Date().toISOString(),
      }));
    } catch (error: any) {
      console.error('Error fetching customer groups:', error);
      // Return empty array as fallback
      return [];
    }
  },

  // Get sales history for a customer (stubbed for now)
  getSalesHistory: async (
    _customerId: string,
    page: number = 1,
    pageSize: number = 10,
    _filters?: SalesHistoryFilters
  ): Promise<SalesHistoryListResponse> => {
    // TODO: Implement when backend has sales history API
    return {
      data: [],
      total: 0,
      page,
      pageSize,
    };
  },

  // Get invoices for a customer (stubbed for now)
  getInvoices: async (
    _customerId: string,
    page: number = 1,
    pageSize: number = 10,
    _filters?: InvoiceFilters
  ): Promise<InvoiceListResponse> => {
    // TODO: Implement when backend has invoices API
    return {
      data: [],
      total: 0,
      page,
      pageSize,
    };
  },

  // Get receivables for a customer (stubbed for now)
  getReceivables: async (
    _customerId: string,
    page: number = 1,
    pageSize: number = 10,
    _filters?: ReceivableFilters
  ): Promise<ReceivableListResponse> => {
    // TODO: Implement when backend has receivables API
    return {
      data: [],
      total: 0,
      page,
      pageSize,
    };
  },

  // Get customer statistics (stubbed for now)
  getCustomerStats: async (): Promise<CustomerStatsResponse> => {
    try {
      // Get basic counts from customers API
      const response = await customerApi.getCustomers(1, 1000); // Get large page to count
      const customers = response.data;
      
      const totalCustomers = response.total;
      const activeCustomers = customers.length; // All fetched customers are active
      const inactiveCustomers = 0;
      const individualCustomers = customers.filter(c => c.customerType === 'individual').length;
      const companyCustomers = customers.filter(c => c.customerType === 'company').length;
      
      const totalSales = customers.reduce((sum, c) => sum + (c.totalPurchases || 0), 0);
      const totalReceivables = customers.reduce((sum, c) => sum + (c.totalDebt || 0), 0);
      const averageOrderValue = customers.length > 0 ? totalSales / customers.length : 0;
      
      const topCustomers = customers
        .sort((a, b) => (b.totalPurchases || 0) - (a.totalPurchases || 0))
        .slice(0, 5);

      return {
        totalCustomers,
        activeCustomers,
        inactiveCustomers,
        individualCustomers,
        companyCustomers,
        totalSales,
        totalReceivables,
        averageOrderValue,
        topCustomers,
      };
    } catch (error) {
      // Return empty stats as fallback
      return {
        totalCustomers: 0,
        activeCustomers: 0,
        inactiveCustomers: 0,
        individualCustomers: 0,
        companyCustomers: 0,
        totalSales: 0,
        totalReceivables: 0,
        averageOrderValue: 0,
        topCustomers: [],
      };
    }
  },

  // Export customer data (stubbed for now)
  exportCustomerData: async (options: ExportCustomerOptions): Promise<Blob> => {
    try {
      // Get all customers
      const response = await customerApi.getCustomers(1, 1000);
      
      const data = JSON.stringify({
        options,
        data: response.data,
        exportedAt: new Date().toISOString(),
      });
      
      return new Blob([data], { type: 'application/json' });
    } catch (error) {
      throw new Error('Failed to export customer data');
    }
  },



  // Get sellers for filter (stubbed for now)
  getSellers: async (): Promise<string[]> => {
    // TODO: Implement when backend has sales/users API
    return ['Seller 1', 'Seller 2', 'Seller 3'];
  },

  // Get customer type statistics for dynamic tabs
  getCustomerTypeStats: async (): Promise<{ customerTypeId: string; customerTypeName: string; count: number }[]> => {
    try {
      const response = await api.get<ApiStandardResponse<ApiCustomerTypeStatsResponse[]>>('/customers/types/stats');

      // Null-safe check
      if (!response.data.data || !Array.isArray(response.data.data)) {
        console.warn('Customer type stats returned null or invalid data');
        return [];
      }

      return response.data.data.map(stat => ({
        customerTypeId: stat.customer_type_id,
        customerTypeName: stat.customer_type_name,
        count: stat.count,
      }));
    } catch (error) {
      console.error('Error fetching customer type statistics:', error);
      // Return empty array as fallback
      return [];
    }
  },

  // Import customers from file
  importCustomers: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/customers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Export customers to file
  exportCustomers: async (format: 'csv' | 'xlsx', filters?: CustomerFilters): Promise<Blob> => {
    const params: Record<string, any> = { format };

    // Add filters to export params
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.customerType && filters.customerType.length > 0) {
      params.customerTypes = filters.customerType.join(',');
    }
    if (filters?.status && filters.status.length > 0) {
      params.status = filters.status.join(',');
    }
    if (filters?.city) {
      params.city = filters.city;
    }

    const queryStr = new URLSearchParams(params).toString();
    const response = await api.get(`/customers/export?${queryStr}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get import template
  getCustomerImportTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/customers/import/template?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Helper to ensure date strings are in RFC3339 format for the backend
  // Converts "YYYY-MM-DD" to "YYYY-MM-DDT00:00:00Z", leaves already-formatted strings as-is
  _toRFC3339: (dateStr: string): string => {
    if (!dateStr) return dateStr;
    // If already contains 'T', assume it's already in ISO/RFC3339 format
    if (dateStr.includes('T')) return dateStr;
    // Append time component for plain date strings (YYYY-MM-DD → YYYY-MM-DDT00:00:00Z)
    return `${dateStr}T00:00:00Z`;
  },

  // Get customer debt report with page-based pagination
  getCustomerDebtReport: async (query: CustomerDebtReportQuery): Promise<CustomerDebtReportResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('date_from', customerApi._toRFC3339(query.dateFrom));
      params.append('date_to', customerApi._toRFC3339(query.dateTo));
      
      if (query.customerId) {
        params.append('customer_id', query.customerId);
      }
      
      if (query.page) {
        params.append('page', query.page.toString());
      }
      
      if (query.pageSize) {
        params.append('page_size', query.pageSize.toString());
      }

      const response = await api.get<ApiStandardResponse<CustomerDebtReportResponse['data']>>(`/customers/debt-report?${params.toString()}`);
      
      // Response structure: { data: { data: [...], total, page, page_size, ... }, timestamp }
      return {
        data: response.data.data,
        timestamp: response.data.timestamp,
      };
    } catch (error: any) {
      console.error('Error fetching customer debt report:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch customer debt report');
    }
  },

  // Get customer debt summary (without transaction details)
  getCustomerDebtSummary: async (query: CustomerDebtReportQuery): Promise<CustomerDebtSummaryResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('date_from', customerApi._toRFC3339(query.dateFrom));
      params.append('date_to', customerApi._toRFC3339(query.dateTo));
      
      if (query.customerId) {
        params.append('customer_id', query.customerId);
      }

      const response = await api.get<ApiStandardResponse<CustomerDebtSummaryResponse>>(`/customers/debt-summary?${params.toString()}`);
      
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching customer debt summary:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch customer debt summary');
    }
  },

  // Assign driver to customer
  assignDriver: async (customerCode: string, driverId: string | null): Promise<Customer> => {
    try {
      const response = await api.put<ApiStandardResponse<ApiCustomerResponse>>(
        `/customers/${customerCode}/assign-driver`,
        { driver_id: driverId }
      );
      return mapApiCustomerToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error assigning driver to customer:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to assign driver');
    }
  },
};